/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Cuenta.
 * Scroll unificado: TicketModuleHeader + PaperSheet nav blocks.
 */
import React, { useEffect, useMemo } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import {
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { accountDeletionHelpUrl, openLegalUrl } from '../../shared/legalUrls';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { CloseBusinessConfirmModal } from '../components/CloseBusinessConfirmModal';
import { DisplayNameEditModal } from '../components/DisplayNameEditModal';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { EmployeeWelcomeCard } from '../../onboarding/components/EmployeeWelcomeCard';
import { ProfileContentSection, type AdministrationGroup } from './components/ProfileContentSection';
import { useProfileScreen } from './useProfileScreen';
import { OrganizationSwitcherSheet } from '../../shell/session/components/OrganizationSwitcherSheet';
import { useOrganizationSwitcherSheet } from '../../shell/session/hooks/useOrganizationSwitcherSheet';

function initialsFromEmail(email: string | null): string {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export const ProfileScreen = () => {
  const vm = useProfileScreen();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const orgSwitcher = useOrganizationSwitcherSheet(navigation);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const onboarding =
    vm.userId && vm.isEmployeeRow === true ? (
      <View style={styles.onboarding}>
        <EmployeeWelcomeCard userId={vm.userId} />
      </View>
    ) : null;

  const administrationGroups: AdministrationGroup[] = useMemo(() => {
    const groups: AdministrationGroup[] = [];

    groups.push({
      id: 'cuenta',
      label: 'Cuenta',
      items: [
        ...(orgSwitcher.canOpenOrganizationMenu
          ? [
              {
                id: 'organizations',
                title: orgSwitcher.organizationCount > 1 ? 'Tus negocios' : 'Tu negocio',
                description:
                  orgSwitcher.organizationCount > 1
                    ? 'Cambiá de negocio o agregá otro.'
                    : 'Agregá otro negocio.',
                onPress: orgSwitcher.open,
                tone: 'default' as const,
                iconName: 'layers-outline' as const,
              },
            ]
          : []),
        ...(vm.isOwner
          ? [
              {
                id: 'display-name',
                title: 'Tu nombre',
                description: vm.displayName
                  ? `${vm.displayName} · Así te ven tus clientes en los turnos.`
                  : 'Así te ven tus clientes en los turnos.',
                onPress: () => vm.setDisplayNameModalOpen(true),
                tone: 'default' as const,
                iconName: 'person-outline' as const,
              },
            ]
          : []),
        ...(vm.canAccessOwnerPanel
          ? [
              {
                id: 'owner-hub-panel',
                title: 'Panel del dueño',
                description: 'Sucursales y resumen.',
                onPress: vm.openOwnerHubPanel,
                tone: 'default' as const,
                iconName: 'grid-outline' as const,
              },
            ]
          : []),
        {
          id: 'activity',
          title: 'Actividad',
          description: 'Sesión, ventas y stock.',
          onPress: vm.openActivityHistory,
          tone: 'default',
          iconName: 'time-outline',
        },
        {
          id: 'marketplace-trust',
          // #1463: "Confianza" no decía qué es — es el estado de la tienda online.
          title: 'Estado de tu tienda online',
          description: vm.marketplaceTrustDescription,
          onPress: vm.openMarketplaceTrust,
          tone: vm.marketplaceTrustNeedsAttention ? 'danger' : 'default',
          iconName: vm.marketplaceTrustNeedsAttention
            ? 'warning-outline'
            : 'shield-checkmark-outline',
        },
        {
          id: 'glossary',
          title: 'Glosario',
          description: 'Stock, caja e informes.',
          onPress: vm.openGlossary,
          tone: 'default',
          iconName: 'book-outline',
        },
      ],
    });

    groups.push({
      id: 'legal',
      label: 'Legal',
      items: [
        {
          id: 'privacy',
          title: 'Privacidad',
          description: 'Cómo cuidamos tus datos.',
          onPress: () => void openLegalUrl('privacy'),
          tone: 'default',
          iconName: 'lock-closed-outline',
        },
        {
          id: 'terms',
          title: 'Términos',
          description: 'Condiciones de uso.',
          onPress: () => void openLegalUrl('terms'),
          tone: 'default',
          iconName: 'document-text-outline',
        },
      ],
    });

    groups.push({
      id: 'sesion',
      label: 'Sesión',
      items: [
        {
          id: 'logout',
          title: 'Cerrar sesión',
          description: 'Salís de este equipo.',
          onPress: () => vm.setLogoutConfirmOpen(true),
          tone: 'default',
          iconName: 'log-out-outline',
        },
        ...(vm.isOwner
          ? [
              {
                id: 'close_business',
                title: vm.deletingAccount ? 'Cerrando…' : 'Cerrar mi negocio',
                description: vm.closeBusinessDescription,
                onPress: vm.openCloseBusinessModal,
                tone: 'danger' as const,
                disabled: vm.deletingAccount || vm.deleteAccountBlocked,
                iconName: 'storefront-outline' as const,
              },
            ]
          : vm.isEmployeeRow === true
            ? [
                {
                  id: 'revoke_employee',
                  title: vm.deletingAccount ? 'Revocando…' : 'Eliminar mi acceso',
                  description: vm.revokeEmployeeDescription,
                  onPress: vm.openRevokeEmployeeModal,
                  tone: 'danger' as const,
                  disabled: vm.deletingAccount || vm.deleteAccountBlocked,
                  iconName: 'person-remove-outline' as const,
                },
              ]
            : []),
      ],
    });

    return groups;
  }, [vm, orgSwitcher.canOpenOrganizationMenu, orgSwitcher.organizationCount, orgSwitcher.open]);

  const headerMeta = useMemo(() => {
    const parts: string[] = [];
    if (vm.roleLabel) parts.push(vm.roleLabel);
    if (vm.organizationName) parts.push(vm.organizationName);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [vm.roleLabel, vm.organizationName]);

  return (
    <>
      <ShellContentFrame style={ticketSurfaces.screen} testID="perfil.screen">
        <ModalAwareScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.content,
            { paddingBottom: insets.bottom + 24 },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={vm.refreshing}
              onRefresh={() => void vm.onRefreshProfile()}
              tintColor={ticketColors.gold}
              colors={[ticketColors.gold]}
            />
          }
        >
          <View style={styles.stack}>
            {/* Header */}
            <TicketModuleHeader
              kicker="Cuenta"
              title={vm.accountEmail ?? 'Sin correo'}
              {...(headerMeta ? { meta: headerMeta } : {})}
              trailing={
                vm.isOwner ? (
                  <View style={styles.ownerBadge}>
                    <Ionicons name="star" size={9} color={ticketColors.gold} />
                    <Text style={styles.ownerBadgeText}>Dueño</Text>
                  </View>
                ) : null
              }
            />

            {/* Onboarding (empleado) */}
            {onboarding}

            {/* Nav groups — stack vertical unificado */}
            <ProfileContentSection administrationGroups={administrationGroups} mobile />
          </View>
        </ModalAwareScrollView>
      </ShellContentFrame>

      {vm.isOwner ? (
        <DisplayNameEditModal
          visible={vm.displayNameModalOpen}
          initialValue={vm.displayName}
          saving={vm.savingDisplayName}
          onSave={(name) => void vm.handleSaveDisplayName(name)}
          onClose={() => vm.setDisplayNameModalOpen(false)}
        />
      ) : null}

      {vm.isOwner ? (
        <CloseBusinessConfirmModal
          visible={vm.closeBusinessModalOpen}
          onClose={() => {
            if (!vm.deletingAccount) vm.setCloseBusinessModalOpen(false);
          }}
          submitting={vm.deletingAccount}
          onConfirmClose={vm.handleConfirmCloseBusiness}
          selectedReason={vm.closeBusinessReason}
          onSelectReason={vm.setCloseBusinessReason}
          accountDeletionHelpUrl={accountDeletionHelpUrl}
        />
      ) : null}

      {vm.isEmployeeRow === true ? (
        <OperationalConfirmModal
          visible={vm.revokeEmployeeModalOpen}
          title="Eliminar mi acceso"
          message="Vas a sacar tu acceso a este negocio. Con el mismo correo seguís comprando en el portal."
          confirmLabel="Sí, revocar acceso"
          iconName="log-out-outline"
          destructive
          loading={vm.deletingAccount}
          onClose={() => {
            if (!vm.deletingAccount) vm.setRevokeEmployeeModalOpen(false);
          }}
          onConfirm={vm.handleConfirmRevokeEmployee}
        />
      ) : null}

      <OperationalConfirmModal
        visible={vm.logoutConfirmOpen}
        title="Cerrar sesión"
        message="Vas a salir de la app."
        confirmLabel="Cerrar sesión"
        iconName="log-out-outline"
        loading={vm.logoutSubmitting}
        onClose={() => {
          if (!vm.logoutSubmitting) vm.setLogoutConfirmOpen(false);
        }}
        onConfirm={vm.handleConfirmLogout}
      />

      {orgSwitcher.canOpenOrganizationMenu ? (
        <OrganizationSwitcherSheet
          visible={orgSwitcher.visible}
          onClose={orgSwitcher.close}
          navigation={orgSwitcher.navigation}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  stack: {
    gap: 10,
    alignSelf: 'stretch',
  },
  onboarding: {
    gap: spacing.md,
    alignSelf: 'stretch',
  },
  ownerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    paddingHorizontal: 8,
    paddingVertical: 3,
    flexShrink: 0,
  },
  ownerBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.brandInk,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontFamily: ticketFonts.sans,
  },
});
